import { useMutation, useQuery } from '@apollo/client';
import { useMemo, useState } from 'react';

import { invalidEmailDomains, invalidRecipients } from '../../../products/voice_agents/utils/recipients.ts';
import {
    CREATE_TRIGGER,
    GET_AGENT_TRIGGER_CHANNELS,
    GET_AGENT_TRIGGERS,
    GET_MY_TRIGGERS,
    GET_TRIGGER_OPTIONS,
    UPDATE_TRIGGER,
} from '../../queries.ts';

export const invalidSenders = (senders: string[]): string[] => invalidEmailDomains(invalidRecipients(senders));
const normaliseSender = (sender: string) => sender.trim().replace(/^@/, '');

// agentId '' = a personal automation (ENG-11406): it runs as the user, in a chat of theirs, on an optional project
export interface EmailAutomationFormState {
    agentId: string;
    projectId: string;
    name: string;
    channelId: string;
    senders: string[];
    instruction: string;
}

interface UseEmailAutomationOptions {
    trigger?: any;
    initialAgentId?: string | null;
    agents: { id: string; name: string }[];
    enabled: boolean;
}

export const useEmailAutomation = ({ trigger, initialAgentId, agents, enabled }: UseEmailAutomationOptions) => {
    const [form, setForm] = useState<EmailAutomationFormState>(() => ({
        agentId: trigger ? (trigger.agentId ?? '') : (initialAgentId ?? agents[0]?.id ?? ''),
        projectId: trigger?.projectId ?? '',
        name: trigger?.name ?? '',
        channelId: trigger?.channelId ?? '',
        senders: trigger?.senders ?? [],
        instruction: trigger?.instruction ?? '',
    }));

    const { data: channelsData } = useQuery(GET_AGENT_TRIGGER_CHANNELS, {
        variables: { agentId: form.agentId || null },
        skip: !enabled,
    });
    const channels = useMemo(() => channelsData?.agentTriggerChannels || [], [channelsData]);

    const { data: optionsData } = useQuery(GET_TRIGGER_OPTIONS, { skip: !enabled });
    const conditionTypes = optionsData?.triggerOptions?.conditionTypes || [];

    const refetchQueries = [
        { query: GET_MY_TRIGGERS },
        ...(form.agentId ? [{ query: GET_AGENT_TRIGGERS, variables: { agentId: form.agentId } }] : []),
    ];
    const [createTrigger] = useMutation(CREATE_TRIGGER, { refetchQueries });
    const [updateTrigger] = useMutation(UPDATE_TRIGGER, { refetchQueries });

    const senders = form.senders.map(normaliseSender).filter(Boolean);
    const valid = Boolean(
        form.name.trim() &&
            form.channelId &&
            senders.length &&
            !invalidSenders(form.senders).length &&
            form.instruction.trim()
    );

    const change = (patch: Partial<EmailAutomationFormState>) =>
        setForm((current) => ({
            ...current,
            ...patch,
            ...('agentId' in patch && patch.agentId !== current.agentId ? { channelId: '' } : {}),
        }));

    const save = async () => {
        const common = {
            channelId: form.channelId,
            documentType: channels.find((channel) => channel.id === form.channelId)?.documentType,
            senders,
            name: form.name.trim(),
            instruction: form.instruction.trim(),
        };
        const projectId = form.agentId ? undefined : form.projectId || null;
        if (trigger) await updateTrigger({ variables: { id: trigger.id, projectId, ...common } });
        else await createTrigger({ variables: { agentId: form.agentId || null, projectId, ...common } });
    };

    return { form, change, channels, conditionTypes, valid, save };
};
